// GIF Session Sidebar for the GIF Creator view
import { useState, useMemo } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Sheet,
  SheetContent,
} from '@/components/ui/sheet'
import { useIsMobile } from '@/hooks/use-mobile'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { 
  Search, 
  FileImage,
  Clock,
  HardDrive,
  X,
  SortAsc,
  SortDesc,
  Plus,
  Pencil,
  Trash2,
  MoreVertical,
  Images,
  Folder
} from 'lucide-react'
import { cn, formatFileSize } from '@/lib/utils'
import { GifSession } from '@/lib/local-types'
import { GifSessionSortOption } from '@/hooks/useGifSessions'

interface GifSessionSidebarProps {
  sessions: GifSession[]
  isLoading: boolean
  currentSessionId: string | null
  storageInfo: {
    used: number
    quota: number
    percentUsed: number
  }
  sortBy: GifSessionSortOption
  onSortChange: (sort: GifSessionSortOption) => void
  searchQuery: string
  onSearchChange: (query: string) => void
  onSelectSession: (session: GifSession) => void
  onCreateSession: (name: string) => Promise<GifSession | null>
  onRenameSession: (sessionId: string, name: string) => Promise<boolean>
  onDeleteSession: (sessionId: string) => Promise<boolean>
  isOpen: boolean
  onOpenChange: (open: boolean) => void
}

export function GifSessionSidebar({
  sessions,
  isLoading,
  currentSessionId,
  storageInfo,
  sortBy,
  onSortChange,
  searchQuery,
  onSearchChange,
  onSelectSession,
  onCreateSession,
  onRenameSession,
  onDeleteSession,
  isOpen,
  onOpenChange,
}: GifSessionSidebarProps) {
  const isMobile = useIsMobile()
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false)
  const [newSessionName, setNewSessionName] = useState('')
  const [isCreating, setIsCreating] = useState(false)

  // Derive sort field and order from sortBy
  const sortField = useMemo(() => {
    if (sortBy.startsWith('date')) return 'date'
    if (sortBy.startsWith('name')) return 'name'
    if (sortBy === 'frames-desc') return 'frames'
    return 'date'
  }, [sortBy])

  const sortOrder = useMemo(() => {
    return sortBy.endsWith('asc') ? 'asc' : 'desc'
  }, [sortBy])

  const handleSortFieldChange = (field: string) => {
    if (field === 'date') {
      onSortChange(sortOrder === 'asc' ? 'date-asc' : 'date-desc')
    } else if (field === 'name') {
      onSortChange(sortOrder === 'asc' ? 'name-asc' : 'name-desc')
    } else if (field === 'frames') {
      onSortChange('frames-desc')
    }
  }

  const toggleSortOrder = () => {
    if (sortField === 'frames') return // Frames only supports desc
    const newOrder = sortOrder === 'asc' ? 'desc' : 'asc'
    if (sortField === 'date') {
      onSortChange(newOrder === 'asc' ? 'date-asc' : 'date-desc')
    } else if (sortField === 'name') {
      onSortChange(newOrder === 'asc' ? 'name-asc' : 'name-desc')
    }
  }

  const handleCreateSession = async () => {
    if (!newSessionName.trim()) return
    setIsCreating(true)
    try {
      const session = await onCreateSession(newSessionName.trim())
      if (session) {
        setNewSessionName('')
        setIsCreateDialogOpen(false)
        onSelectSession(session)
      }
    } finally {
      setIsCreating(false)
    }
  }

  return (
    <>
      {/* Mobile: Sheet sidebar */}
      <Sheet open={isOpen && isMobile} onOpenChange={onOpenChange}>
        <SheetContent side="left" className="w-[85vw] max-w-80 p-0 md:hidden">
          <div className="flex flex-col h-full">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b">
              <div className="flex items-center gap-2">
                <Folder className="h-5 w-5" />
                <h2 className="font-semibold">GIF Sessions</h2>
                <Badge variant="secondary" className="ml-1">
                  {sessions.length}
                </Badge>
              </div>
            </div>

            {/* Storage info */}
            <div className="px-4 py-2 border-b bg-muted/30">
              <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
                <span className="flex items-center gap-1">
                  <HardDrive className="h-3 w-3" />
                  Storage
                </span>
                <span>{formatFileSize(storageInfo.used)} / {formatFileSize(storageInfo.quota)}</span>
              </div>
              <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                <div
                  className={cn(
                    'h-full rounded-full transition-all',
                    storageInfo.percentUsed > 90 ? 'bg-destructive' :
                    storageInfo.percentUsed > 70 ? 'bg-yellow-500' : 'bg-primary'
                  )}
                  style={{ width: `${Math.min(100, storageInfo.percentUsed)}%` }}
                />
              </div>
            </div>

            {/* Search and filters */}
            <div className="p-3 border-b space-y-2">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search sessions..."
                  value={searchQuery}
                  onChange={(e) => onSearchChange(e.target.value)}
                  className="pl-9 pr-8 h-9"
                />
                {searchQuery && (
                  <button
                    onClick={() => onSearchChange('')}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Select value={sortField} onValueChange={handleSortFieldChange}>
                  <SelectTrigger className="h-8 flex-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="date">Last Updated</SelectItem>
                    <SelectItem value="name">Name</SelectItem>
                    <SelectItem value="frames">Frame Count</SelectItem>
                  </SelectContent>
                </Select>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-8 w-8 flex-shrink-0"
                  onClick={toggleSortOrder}
                  disabled={sortField === 'frames'}
                >
                  {sortOrder === 'asc' ? <SortAsc className="h-4 w-4" /> : <SortDesc className="h-4 w-4" />}
                </Button>
              </div>
            </div>

            {/* Session list */}
            <ScrollArea className="flex-1 [&_[data-slot=scroll-area-viewport]>div]:!block">
              <div className="p-2 space-y-1">
                {isLoading ? (
                  Array.from({ length: 5 }).map((_, i) => (
                    <div key={i} className="p-2 space-y-2">
                      <Skeleton className="h-4 w-3/4" />
                      <Skeleton className="h-3 w-1/2" />
                    </div>
                  ))
                ) : sessions.length === 0 ? (
                  <div className="py-8 text-center">
                    {searchQuery ? (
                      <>
                        <Search className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                        <p className="text-sm text-muted-foreground">No sessions match your search</p>
                        <Button variant="link" size="sm" onClick={() => onSearchChange('')}>Clear search</Button>
                      </>
                    ) : (
                      <>
                        <FileImage className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                        <p className="text-sm text-muted-foreground mb-3">No GIF sessions yet</p>
                        <Button variant="outline" size="sm" onClick={() => setIsCreateDialogOpen(true)} className="gap-2">
                          <Plus className="h-4 w-4" /> Create Session
                        </Button>
                      </>
                    )}
                  </div>
                ) : (
                  sessions.map((session) => (
                    <SessionItem
                      key={session.id}
                      session={session}
                      isSelected={session.id === currentSessionId}
                      onClick={() => { onSelectSession(session); onOpenChange(false); }}
                      onRename={(name) => onRenameSession(session.id, name)}
                      onDelete={() => onDeleteSession(session.id)}
                    />
                  ))
                )}
              </div>
            </ScrollArea>

            {/* Create new session footer */}
            <div className="p-3 border-t">
              <Button variant="outline" className="w-full gap-2" onClick={() => setIsCreateDialogOpen(true)}>
                <Plus className="h-4 w-4" /> New GIF Session
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* Desktop: permanent docked panel */}
      <aside className="hidden md:flex flex-col w-80 shrink-0 min-h-0 bg-background border-r">
        <div className="flex flex-col h-full w-80">
          {/* Header */}
          <div className="flex items-center gap-2 p-4 border-b">
            <Folder className="h-5 w-5" />
            <h2 className="font-semibold">GIF Sessions</h2>
            <Badge variant="secondary" className="ml-1">
              {sessions.length}
            </Badge>
          </div>

          {/* Storage info */}
          <div className="px-4 py-2 border-b bg-muted/30">
            <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
              <span className="flex items-center gap-1">
                <HardDrive className="h-3 w-3" />
                Storage
              </span>
              <span>{formatFileSize(storageInfo.used)} / {formatFileSize(storageInfo.quota)}</span>
            </div>
            <div className="h-1.5 bg-muted rounded-full overflow-hidden">
              <div
                className={cn(
                  'h-full rounded-full transition-all',
                  storageInfo.percentUsed > 90 ? 'bg-destructive' :
                  storageInfo.percentUsed > 70 ? 'bg-yellow-500' : 'bg-primary'
                )}
                style={{ width: `${Math.min(100, storageInfo.percentUsed)}%` }}
              />
            </div>
          </div>

          {/* Search and filters */}
          <div className="p-3 border-b space-y-2">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search sessions..."
                value={searchQuery}
                onChange={(e) => onSearchChange(e.target.value)}
                className="pl-9 pr-8 h-9"
              />
              {searchQuery && (
                <button
                  onClick={() => onSearchChange('')}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>

            {/* Sort controls */}
            <div className="flex items-center gap-2">
              <Select value={sortField} onValueChange={handleSortFieldChange}>
                <SelectTrigger className="h-8 flex-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="date">Last Updated</SelectItem>
                  <SelectItem value="name">Name</SelectItem>
                  <SelectItem value="frames">Frame Count</SelectItem>
                </SelectContent>
              </Select>
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8 flex-shrink-0"
                onClick={toggleSortOrder}
                disabled={sortField === 'frames'}
              >
                {sortOrder === 'asc' ? (
                  <SortAsc className="h-4 w-4" />
                ) : (
                  <SortDesc className="h-4 w-4" />
                )}
              </Button>
            </div>
          </div>

          {/* Session list */}
          <ScrollArea className="flex-1 [&_[data-slot=scroll-area-viewport]>div]:!block">
            <div className="p-2 space-y-1">
              {isLoading ? (
                // Loading skeletons
                Array.from({ length: 5 }).map((_, i) => (
                  <div key={i} className="p-2 space-y-2">
                    <Skeleton className="h-4 w-3/4" />
                    <Skeleton className="h-3 w-1/2" />
                  </div>
                ))
              ) : sessions.length === 0 ? (
                // Empty state
                <div className="py-8 text-center">
                  {searchQuery ? (
                    <>
                      <Search className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                      <p className="text-sm text-muted-foreground">No sessions match your search</p>
                      <Button
                        variant="link"
                        size="sm"
                        onClick={() => onSearchChange('')}
                      >
                        Clear search
                      </Button>
                    </>
                  ) : (
                    <>
                      <FileImage className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                      <p className="text-sm text-muted-foreground mb-3">No GIF sessions yet</p>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setIsCreateDialogOpen(true)}
                        className="gap-2"
                      >
                        <Plus className="h-4 w-4" />
                        Create Session
                      </Button>
                    </>
                  )}
                </div>
              ) : (
                // Session items
                sessions.map((session) => (
                  <SessionItem
                    key={session.id}
                    session={session}
                    isSelected={session.id === currentSessionId}
                    onClick={() => onSelectSession(session)}
                    onRename={(name) => onRenameSession(session.id, name)}
                    onDelete={() => onDeleteSession(session.id)}
                  />
                ))
              )}
            </div>
          </ScrollArea>

          {/* Create new session footer */}
          <div className="p-3 pb-16 border-t">
            <Button
              variant="outline"
              className="w-full gap-2"
              onClick={() => setIsCreateDialogOpen(true)}
            >
              <Plus className="h-4 w-4" />
              New GIF Session
            </Button>
          </div>
        </div>
      </aside>

      {/* Create session dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New GIF Session</DialogTitle>
            <DialogDescription>
              A session lets you collect and organize frames for GIF creation.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Input
              placeholder="Session name..."
              value={newSessionName}
              onChange={(e) => setNewSessionName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleCreateSession()
              }}
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateSession} disabled={isCreating || !newSessionName.trim()}>
              {isCreating ? 'Creating...' : 'Create Session'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

// Individual session item component
interface SessionItemProps {
  session: GifSession
  isSelected: boolean
  onClick: () => void
  onRename: (name: string) => Promise<boolean>
  onDelete: () => Promise<boolean>
}

function SessionItem({ session, isSelected, onClick, onRename, onDelete }: SessionItemProps) {
  const [isRenameDialogOpen, setIsRenameDialogOpen] = useState(false)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [newName, setNewName] = useState(session.name)
  const [isRenaming, setIsRenaming] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)

  const updatedDate = new Date(session.updatedAt)
  const formattedDate = updatedDate.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: updatedDate.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined
  })

  const handleRename = async () => {
    if (!newName.trim() || newName.trim() === session.name) {
      setIsRenameDialogOpen(false)
      return
    }
    setIsRenaming(true)
    try {
      await onRename(newName.trim())
      setIsRenameDialogOpen(false)
    } finally {
      setIsRenaming(false)
    }
  }

  const handleDelete = async () => {
    setIsDeleting(true)
    try {
      await onDelete()
      setIsDeleteDialogOpen(false)
    } finally {
      setIsDeleting(false)
    }
  }

  // Get first frame thumbnail if available
  const thumbnailUrl = session.frames.length > 0 ? session.frames[0].url : null

  return (
    <>
      <div
        className={cn(
          'w-full text-left p-3 rounded-lg transition-colors group relative',
          'hover:bg-muted/50',
          isSelected && 'bg-primary/10 border border-primary/30 hover:bg-primary/15'
        )}
      >
        <button
          onClick={onClick}
          className="w-full text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-lg"
        >
          <div className="flex items-start gap-3">
            {/* Thumbnail */}
            <div className={cn(
              'w-14 h-10 rounded bg-muted flex-shrink-0 flex items-center justify-center overflow-hidden',
              isSelected && 'ring-2 ring-primary'
            )}>
              {thumbnailUrl ? (
                <img 
                  src={thumbnailUrl} 
                  alt="" 
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    // Hide broken image gracefully
                    (e.target as HTMLImageElement).style.display = 'none';
                  }}
                />
              ) : (
                <Images className="h-4 w-4 text-muted-foreground" />
              )}
            </div>

            <div className="flex-1 min-w-0 pr-8">
              {/* Session name - allow 2 lines then truncate */}
              <p className={cn(
                'font-medium text-sm leading-tight break-words line-clamp-2',
                isSelected && 'text-primary'
              )}>
                {session.name}
              </p>

              {/* Metadata */}
              <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                <span className="flex items-center gap-1 shrink-0">
                  <Images className="h-3 w-3" />
                  {session.frames.length}
                </span>
                <span className="shrink-0">•</span>
                <span className="shrink-0">{formatFileSize(session.totalSize)}</span>
              </div>

              {/* Updated date */}
              <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1">
                <Clock className="h-3 w-3 shrink-0" />
                <span className="truncate">{formattedDate}</span>
              </p>
            </div>
          </div>
        </button>

        {/* Actions menu */}
        <div className="absolute right-2 top-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => {
                setNewName(session.name)
                setIsRenameDialogOpen(true)
              }}>
                <Pencil className="h-4 w-4 mr-2" />
                Rename
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem 
                onClick={() => setIsDeleteDialogOpen(true)}
                className="text-destructive focus:text-destructive"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Selected indicator */}
        {isSelected && (
          <Badge variant="default" className="absolute right-2 bottom-2 text-xs">
            Active
          </Badge>
        )}
      </div>

      {/* Rename dialog */}
      <Dialog open={isRenameDialogOpen} onOpenChange={setIsRenameDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rename Session</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleRename()
              }}
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsRenameDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleRename} disabled={isRenaming}>
              {isRenaming ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation dialog */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Session</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{session.name}"? This will permanently delete all {session.frames.length} frames in this session. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}

